import { serverNow } from "../../../../../../shared/frontend/time/serverClock";
import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../../app/auth/useAuth";
import { useRealtimeEvent } from "../../app/realtime/RealtimeContext";
import { RealtimeEventTypes } from "../../app/realtime/realtimeEventTypes";

/** Puffer nach Ablauf der Statusgrenze, damit der Server sie sicher überschritten hat. */
const REFETCH_MARGIN_MS = 100;

export type AccessMediumAssignmentReader = {
  id: string;
  deviceName: string;
  hardwareName: string;
  online: boolean;
  onlineUntil?: string | null;
};

const emptyReaders: AccessMediumAssignmentReader[] = [];

/** Anzeige und Scan bekommen dieselbe freigegebene Lesergruppe eines konkreten Standorts. */
export function useAccessMediumAssignmentReaders(studioId: string | null, enabled: boolean) {
  const auth = useAuth();
  const query = useQuery({
    queryKey: ["access-medium-assignment-readers", auth.info?.tenantId, auth.info?.accountId, studioId],
    enabled: enabled && !!studioId,
    queryFn: ({ signal }) => auth.api.get<AccessMediumAssignmentReader[]>(
      `/api/v1/access-media/assignment-readers?studioId=${encodeURIComponent(studioId!)}`,
      { signal, suppressGlobalErrorToast: true }
    )
  });
  const refresh = () => { void query.refetch(); };
  useRealtimeEvent(RealtimeEventTypes.ReaderDevicesStatus, refresh, { enabled: enabled && !!studioId, onResume: refresh });
  useRealtimeEvent(RealtimeEventTypes.ReaderDevicesChanged, refresh, { enabled: enabled && !!studioId, onResume: refresh });
  const readers = query.data ?? emptyReaders;
  const { refetch } = query;
  React.useEffect(() => {
    if (!enabled || !studioId) return;
    const expiries = readers.filter(reader => reader.online && reader.onlineUntil)
      .map(reader => Date.parse(reader.onlineUntil!)).filter(Number.isFinite);
    if (expiries.length === 0) return;
    // Einmalig an der serverseitigen Statusgrenze aktualisieren, kein Netzwerk-Polling.
    const timer = window.setTimeout(() => { void refetch(); }, Math.max(0, Math.min(...expiries) - serverNow()) + REFETCH_MARGIN_MS);
    return () => window.clearTimeout(timer);
  }, [enabled, studioId, readers, refetch]);
  const readerIds = React.useMemo(() => readers.map(reader => reader.id).sort(), [readers]);
  return { ...query, readers, readerIds };
}
